import _Fab from "@mui/material/Fab";
import _Tooltip from "@mui/material/Tooltip";
import { ThemeProvider, alpha } from '@mui/material/styles';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { styled } from 'styled-components';
import pegman from '../../assets/images/pegman.png';

// Below this a pointer movement is a click, not a drag.
const dragThreshold = 5;

/**
 * Street-level imagery "pegman": a click toggles the imagery coverage on/off, and dragging it
 * reveals the coverage, previews the picture under the pointer, and opens a viewer where it is
 * dropped.
 *
 * Provider-agnostic — the tooltips and the zoom threshold below which browsing is pointless are
 * injected. Ids are namespaced per instance so several pegmen can coexist on one page.
 */
export class PegmanControl {
  prevX = null;
  dragging = false;
  startX = null;
  startY = null;
  locked = false;
  active = false;
  constructor(name = 'pegman', {
    minZoom = 14
  } = {}) {
    this.minZoom = minZoom;
    this.containerId = `${name}-control`;
    this.tooltipId = `${name}-control-tooltip`;
    this.buttonId = `${name}-control-button`;
    this.pegmanId = `${name}-control-pegman`;
  }
  onAdd(_map) {
    this.map = _map;
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl';
    const content = document.createElement('div');
    content.id = this.containerId;
    this.container.appendChild(content);
    return this.container;
  }
  addGrabbingClass(_element) {
    const element = typeof _element === 'string' ? document.getElementById(_element) : _element;
    if (element) element.classList.add('grabbing');
  }
  removeGrabbingClass(_element) {
    const element = typeof _element === 'string' ? document.getElementById(_element) : _element;
    if (element) element.classList.remove('grabbing', 'grabbing-left', 'grabbing-right');
  }
  handleDrag = ({
    clientX,
    clientY
  }) => {
    const buttonElement = document.getElementById(this.buttonId);
    const pegmanElement = document.getElementById(this.pegmanId);
    if (!buttonElement || !pegmanElement) return;
    const {
      x,
      y
    } = buttonElement.getBoundingClientRect();
    if (this.prevX === null) {
      buttonElement.classList.add('grabbing-left');
    } else if (this.prevX < clientX) {
      buttonElement.classList.remove('grabbing-left');
      buttonElement.classList.add('grabbing-right');
    } else if (this.prevX > clientX) {
      buttonElement.classList.remove('grabbing-right');
      buttonElement.classList.add('grabbing-left');
    }
    pegmanElement.style.transform = `translate(${clientX - x - 20}px, ${clientY - y - 40}px)`;
    this.prevX = clientX;
  };
  cleanupPointerListeners() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerCancel);
  }
  finishDrag() {
    const result = this.callbacks?.onDragEnd();
    if (this.map) this.removeGrabbingClass(this.map.getContainer().getElementsByTagName('canvas')[0]);
    this.removeGrabbingClass(this.buttonId);
    this.prevX = null;

    // Deferred to the callback's resolution rather than reset on the spot: whether a picture was
    // found there decides if the pegman snaps back straight or bounces back into its button.
    Promise.resolve(result).then(found => {
      const element = document.getElementById(this.pegmanId);
      if (!element) return;
      if (!found) element.classList.add('returning');
      element.style.transform = 'translate(0, 0)';
      if (!found) {
        element.addEventListener('transitionend', () => element.classList.remove('returning'), {
          once: true
        });
      }
    });
  }
  handlePointerMove = event => {
    if (!this.dragging) {
      if (this.startX === null || this.startY === null) return;
      const distance = Math.hypot(event.clientX - this.startX, event.clientY - this.startY);
      if (distance < dragThreshold) return;
      this.dragging = true;
      this.callbacks?.onDragStart();
      if (this.map) this.addGrabbingClass(this.map.getContainer().getElementsByTagName('canvas')[0]);
      this.addGrabbingClass(this.buttonId);
      const tooltipElement = document.getElementById(this.tooltipId);
      if (tooltipElement) tooltipElement.style.display = 'none';

      // In case a bounce-back from the previous drop is still mid-animation.
      document.getElementById(this.pegmanId)?.classList.remove('returning');
    }
    this.handleDrag(event);
  };
  handlePointerUp = () => {
    this.cleanupPointerListeners();
    if (this.dragging) this.finishDrag();else this.callbacks?.onToggle();
    this.dragging = false;
    this.startX = null;
    this.startY = null;
  };
  handlePointerCancel = () => {
    this.cleanupPointerListeners();

    // A cancelled gesture never completes a click, so it never toggles.
    if (this.dragging) this.finishDrag();
    this.dragging = false;
    this.startX = null;
    this.startY = null;
  };
  handlePointerDown = ({
    button,
    clientX,
    clientY,
    currentTarget,
    pointerId
  }) => {
    if (button !== 0) return;

    // Keeps the gesture targeting this button even if the pointer strays over the map or another
    // element mid-gesture — touch drags are otherwise prone to losing the pointer.
    currentTarget.setPointerCapture(pointerId);
    this.dragging = false;
    this.startX = clientX;
    this.startY = clientY;
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerCancel);
  };
  init(theme, titles, callbacks) {
    this.theme = theme;
    this.titles = titles;
    this.callbacks = callbacks;
    this.update();
  }
  update() {
    if (!this.map || !this.theme) return;
    const element = document.getElementById(this.containerId);
    if (!element) return;
    this.root = this.root || createRoot(element);
    const activeClass = this.active ? 'active' : '';
    if (this.map.getZoom() < this.minZoom) {
      this.root.render(/*#__PURE__*/React.createElement(ThemeProvider, {
        theme: this.theme
      }, /*#__PURE__*/React.createElement(_Tooltip, {
        id: this.tooltipId,
        placement: "left",
        title: this.titles?.tooFar || ''
      }, /*#__PURE__*/React.createElement(StyledFab, {
        disableRipple: true,
        className: "too-far",
        id: this.buttonId,
        size: "small"
      }, /*#__PURE__*/React.createElement(StyledPegman, {
        className: "pegman",
        id: this.pegmanId
      })))));
    } else if (this.locked) {
      this.root.render(/*#__PURE__*/React.createElement(ThemeProvider, {
        theme: this.theme
      }, /*#__PURE__*/React.createElement(_Tooltip, {
        id: this.tooltipId,
        placement: "left",
        title: this.titles?.locked || ''
      }, /*#__PURE__*/React.createElement(StyledFab, {
        className: `locked ${activeClass}`,
        id: this.buttonId,
        onPointerDown: this.handlePointerDown,
        size: "small"
      }, /*#__PURE__*/React.createElement(StyledPegman, {
        className: "pegman",
        id: this.pegmanId
      })))));
    } else {
      this.root.render(/*#__PURE__*/React.createElement(ThemeProvider, {
        theme: this.theme
      }, /*#__PURE__*/React.createElement(_Tooltip, {
        id: this.tooltipId,
        placement: "left",
        title: this.titles?.unlocked || ''
      }, /*#__PURE__*/React.createElement(StyledFab, {
        className: activeClass,
        id: this.buttonId,
        onPointerDown: this.handlePointerDown,
        size: "small"
      }, /*#__PURE__*/React.createElement(StyledPegman, {
        className: "pegman",
        id: this.pegmanId
      })))));
    }
  }
  lock() {
    this.locked = true;
    this.update();
  }
  unlock() {
    this.locked = false;
    this.update();
  }
  setActive(active) {
    this.active = active;
    this.update();
  }
  onRemove() {
    this.cleanupPointerListeners();
    const root = this.root;
    this.root = undefined;
    // Unmounting synchronously here would happen while React is already rendering.
    if (root) setTimeout(() => root.unmount(), 0);
    this.container?.parentNode?.removeChild(this.container);
    this.map = undefined;
  }
}
const StyledFab = styled(_Fab)`
  && {
    background-color: #fff;
    cursor: grab;
    touch-action: none;

    &:not(:disabled):hover {
      background-color: #d5d5d5;
    }

    &.active {
      background-color: #fce3ff;

      &:not(:disabled):hover {
        background-color: #fce3ff;
      }
    }

    &:not(.too-far):hover {
      .pegman::before {
        background-position: -48px 0;
      }
    }

    &.too-far {
      background-color: #d5d5d5;
      cursor: default;

      &:hover {
        background-color: #d5d5d5;
      }

      &:active {
        box-shadow:
          0 3px 5px -1px rgb(0 0 0 / 20%),
          0 6px 10px 0 rgb(0 0 0 / 14%),
          0 1px 18px 0 rgb(0 0 0 / 12%);
      }
    }

    &.grabbing {
      cursor: grabbing;

      &::before {
        background-image: ${`url(${pegman})`};
        background-position: -24px 0;
        background-repeat: no-repeat;
        background-size: auto 24px;
        content: ' ';
        display: block;
        height: 24px;
        left: 8px;
        position: absolute;
        top: 8px;
        width: 24px;
      }

      .pegman::before {
        background-position: -72px 0;
      }

      &.grabbing-right {
        .pegman::before {
          background-position: -96px 0;
        }
      }

      .pegman::after {
        opacity: 1;
      }
    }

    /* Deliberately keeps the default green sprite: viewing a picture is not a disabled state, the
       pegman stays available to be dragged somewhere else. */
    &.locked {
      cursor: grab;
    }

    .MuiFab-label {
      height: 100%;
    }
  }
`;
const StyledPegman = styled.div`
  height: 40px;
  position: relative;
  width: 40px;

  /* Dropped where there is no imagery to show: bounces back into the button instead of the
     ordinary instant snap, echoing the "nothing here" outcome. */
  &.returning {
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &::before {
    background-image: ${`url(${pegman})`};
    background-position: 0 0;
    background-repeat: no-repeat;
    background-size: auto 24px;
    content: ' ';
    display: block;
    height: 24px;
    left: 8px;
    position: absolute;
    top: 8px;
    width: 24px;
  }

  &::after {
    display: block;
    border: 2px solid white;
    background-color: ${alpha('#659F4A', 0.8)};
    border-radius: 50%;
    box-shadow: 3px 10px 25px rgb(105 185 70 / 90%);
    content: ' ';
    height: 6px;
    left: 16px;
    opacity: 0;
    overflow: visible;
    position: absolute;
    top: 36px;
    visibility: visible;
    width: 6px;
  }
`;